import { DIFF_TYPE } from '../../../../../../constnats/diff';
import type {
    DescriptorDiffType,
    PropDiffChangedType,
    PropDiffType,
    TableDiffType,
} from '../../../../../../schemas/schemaDiff.schemas';
import { MODEL_DIFF_PROPERTY, REPORT_FIELD_DIFF_PROPERTY } from './constants';
import type { ResolvedTableDiffType } from './types';

export function hasTableDiffs(tableDiff: TableDiffType): boolean {
    return tableDiff.schemaDiff.diffs.length > 0 || tableDiff.reportsDiff.reports.length > 0;
}

export function isPropDiffChanged(diff: PropDiffType): diff is PropDiffChangedType {
    return 'referenceVal' in diff && 'targetVal' in diff;
}

export function shouldDisplayPropertyDiff(
    property: string,
    diff: PropDiffType,
    coreDataDiffOnly = false,
): diff is PropDiffChangedType {
    return (
        isPropDiffChanged(diff) &&
        (!coreDataDiffOnly || property === REPORT_FIELD_DIFF_PROPERTY.CORE_VALUE)
    );
}

type TableDiffFilterOptions = {
    coreDataDiffOnly?: boolean;
    ignoredModelDiffProperties?: readonly string[];
    headlineDescriptorIds?: readonly string[];
};

function isHeadlineWithOnlyVerticalPositionChange(
    descriptorDiff: DescriptorDiffType,
    headlineDescriptorIds: ReadonlySet<string>,
): boolean {
    if (!headlineDescriptorIds.has(descriptorDiff.descriptorId)) {
        return false;
    }

    const changedProperties = Object.entries(descriptorDiff.diffs ?? {})
        .filter(([, diff]) => isPropDiffChanged(diff))
        .map(([property]) => property);

    return changedProperties.length === 1
        && changedProperties.includes(MODEL_DIFF_PROPERTY.VERTICAL_POSITION);
}

export function filterTableDiff(
    tableDiff: ResolvedTableDiffType,
    {
        coreDataDiffOnly = false,
        ignoredModelDiffProperties = [],
        headlineDescriptorIds = [],
    }: TableDiffFilterOptions = {},
): ResolvedTableDiffType {
    const ignoredModelDiffPropertiesSet = new Set(ignoredModelDiffProperties);
    const headlineDescriptorIdsSet = new Set(headlineDescriptorIds);
    const modelDiffs = tableDiff.schemaDiff.diffs
        .map((descriptorDiff) => {
            if (descriptorDiff.diffType !== DIFF_TYPE.CHANGED || descriptorDiff.diffs == null) {
                return descriptorDiff;
            }

            const propertyEntries = Object.entries(descriptorDiff.diffs);
            if (propertyEntries.length === 0) {
                return descriptorDiff;
            }

            if (isHeadlineWithOnlyVerticalPositionChange(descriptorDiff, headlineDescriptorIdsSet)) {
                return null;
            }

            const visiblePropertyDiffs = Object.fromEntries(
                propertyEntries.filter(
                    ([property, diff]) =>
                        !ignoredModelDiffPropertiesSet.has(property) && isPropDiffChanged(diff),
                ),
            );

            return Object.keys(visiblePropertyDiffs).length > 0
                ? { ...descriptorDiff, diffs: visiblePropertyDiffs }
                : null;
        })
        .filter((descriptorDiff): descriptorDiff is NonNullable<typeof descriptorDiff> => descriptorDiff !== null);

    const reportDiffs = tableDiff.reportsDiff.reports
        .map((reportDiff) => {
            if (reportDiff.diffType !== DIFF_TYPE.CHANGED) {
                return reportDiff;
            }

            const visibleFieldDiffs = Object.fromEntries(
                Object.entries(reportDiff.fieldDiffs)
                    .map(([descriptorId, propertyDiffs]) => [
                        descriptorId,
                        Object.fromEntries(
                            Object.entries(propertyDiffs).filter(([property, diff]) =>
                                shouldDisplayPropertyDiff(property, diff, coreDataDiffOnly),
                            ),
                        ),
                    ] as const)
                    .filter(([, propertyDiffs]) => Object.keys(propertyDiffs).length > 0),
            );

            return Object.keys(visibleFieldDiffs).length > 0
                ? { ...reportDiff, fieldDiffs: visibleFieldDiffs }
                : null;
        })
        .filter((reportDiff): reportDiff is NonNullable<typeof reportDiff> => reportDiff !== null);

    return {
        ...tableDiff,
        schemaDiff: {
            ...tableDiff.schemaDiff,
            diffs: modelDiffs,
        },
        reportsDiff: {
            ...tableDiff.reportsDiff,
            reports: reportDiffs,
        },
    };
}
